import React, { useEffect, useState } from "react";
import { Box, TextField, Autocomplete, Typography, Grid } from "@mui/material";
import "./index.css";
import { DatePicker } from "@mui/x-date-pickers";
import {
  Collection,
  CollectionSite,
  TrapType,
  CollectionFormData,
} from "../../../types";
import { useAppDispatch, useAppSelector } from "../../../stores/store";
import type { Dayjs } from "dayjs";
import dayjs from "dayjs";
import { updateCollection } from "../../../stores/collection/actions";
import { useWebServer } from "../../../providers";

interface props {
  collection: Collection;
}

const CollectionForm: React.FC<props> = ({ collection }) => {
  const dispatch = useAppDispatch();
  const collectionSitesOptions = useAppSelector(
    (state) => state.collectionSites.collectionSites
  );
  const trapTypesOptions = useAppSelector(
    (state) => state.collectionSites.trapTypes
  );
  const [lastCollection, setLastCollection] = useState<string>("");
  const { sendRequest } = useWebServer();

  const [formData, setFormData] = React.useState<CollectionFormData>({
    collectionDate: null,
    trapTypeId: null,
    collectionSiteId: null,
    description: null,
  });

  const [timer, setTimer] = useState<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (lastCollection !== collection?.id) {
      setFormData({
        collectionSiteId:
          collectionSitesOptions.filter(
            (cur) => cur.id === collection.collectionSiteId
          )[0] || null,
        trapTypeId:
          trapTypesOptions.filter(
            (cur) => cur.id === collection.trapTypeId
          )[0] || null,
        collectionDate: collection?.collectionDate
          ? dayjs(collection.collectionDate)
          : null,
        description: collection.description,
        id: collection.id,
      });
      setLastCollection(collection?.id);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [collection]);

  const handleDateChange =
    (name: keyof CollectionFormData) =>
    (newValue: Dayjs | null | TrapType | CollectionSite | null) => {
      setFormData({
        ...formData,
        [name]: newValue,
      });

      delayedSubmit({
        ...formData,
        [name]: newValue,
      });
    };

  const handleAutocompleteChange =
    (name: keyof CollectionFormData) =>
    (_: React.SyntheticEvent, value: TrapType | CollectionSite | null) => {
      setFormData({
        ...formData,
        [name]: value,
      });

      delayedSubmit({
        ...formData,
        [name]: value,
      });
    };

  const handleTextChange =
    (field: keyof CollectionFormData) =>
    (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setFormData({
        ...formData,
        [field]: event.target.value,
      });
      delayedSubmit({
        ...formData,
        [field]: event.target.value,
      });
    };

  const delayedSubmit = (formData: CollectionFormData) => {
    if (timer) {
      clearTimeout(timer);
    }
    // Set a new timer to submit the form after 0.5 seconds
    setTimer(
      setTimeout(() => {
        handleSubmit(formData);
      }, 500)
    );
  };

  const handleSubmit = (formData: CollectionFormData) => {
    dispatch(updateCollection({ sendRequest, collection: formData }));
  };

  return (
    <Box component="form" className={"collection-detail-form"}>
      <Typography className="collection-detail-form-title">
        Collection Details:
      </Typography>
      <Grid container spacing={2}>
        <Grid size={{ xs: 6, md: 6, lg: 4 }}>
          <DatePicker
            label="Collection Date"
            slotProps={{
              field: { clearable: true, onClear: () => {} },
            }}
            defaultValue={null}
            value={formData?.collectionDate}
            onChange={handleDateChange("collectionDate")}
          />
        </Grid>
        <Grid size={{ xs: 6, md: 6, lg: 4 }}>
          <Autocomplete
            disablePortal
            options={trapTypesOptions}
            sx={{ width: 300 }}
            getOptionLabel={(cur) => cur.displayName || ""}
            getOptionKey={(cur) => cur.id || ""}
            value={formData.trapTypeId}
            isOptionEqualToValue={(option, value) => option?.id === value?.id}
            renderInput={(params) => (
              <TextField {...params} label="Collection Method" />
            )}
            onChange={handleAutocompleteChange("trapTypeId")}
          />
        </Grid>
        <Grid size={{ xs: 6, md: 6, lg: 4 }}>
          <Autocomplete
            disablePortal
            options={collectionSitesOptions}
            sx={{ width: 300 }}
            getOptionLabel={(cur) => cur.displayName || ""}
            value={formData.collectionSiteId}
            renderInput={(params) => (
              <TextField {...params} label="Collection Site" />
            )}
            isOptionEqualToValue={(option, value) => option?.id === value?.id}
            onChange={handleAutocompleteChange("collectionSiteId")}
          />
        </Grid>
        <Grid size={{ xs: 6, md: 6, lg: 12 }}>
          <TextField
            label="Description"
            fullWidth
            value={formData.description}
            onChange={handleTextChange("description")}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default CollectionForm;
